import React, { useEffect, useMemo } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { useLocation } from "react-router-dom";

const RECAPTCHA_ROUTES = [
  "/login",
  "/register",
  "/add-brand",
  "/forgot-password",
  "/update-password",
];

/**
 * A component that conditionally wraps its children with GoogleReCaptchaProvider based on the current route.
 * The reCAPTCHA badge is shown or hidden depending on whether the current route requires reCAPTCHA.
 *
 * @param {object} props - The component props.
 * @param {import("react").ReactNode} props.children - The child components to be conditionally wrapped.
 * @returns {import("react").ReactElement} The children wrapped with GoogleReCaptchaProvider if the route matches, otherwise just the children.
 */
export default function RecaptchaProvider(props) {
  const { children } = props;
  const location = useLocation();

  const needsRecaptcha = useMemo(
    () => RECAPTCHA_ROUTES.some((route) => location.pathname.includes(route)),
    [location.pathname],
  );

  // Set visibility of reCAPTCHA badge based on whether it's needed
  useEffect(() => {
    // Try to find the badge and set its visibility
    const updateVisibility = () => {
      const badgeEl = document.querySelector(".grecaptcha-badge");
      if (badgeEl) {
        badgeEl.style.visibility = needsRecaptcha ? "visible" : "hidden";
      }
    };

    updateVisibility();

    const observer = new MutationObserver(() => updateVisibility());
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [needsRecaptcha]);

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
      scriptProps={{ async: true, defer: true }}
      useEnterprise
    >
      {children}
    </GoogleReCaptchaProvider>
  );
}
